import { parseGoogleAddressComponents, googlePlaceToPoi } from "@/components/organisms/MyFavourites/FavouriteLocationsTable/suggestions";
import { FlightArrivalTerminal, flightTerminalLabel, terminalFromText } from "@/helpers/bookingHelpers";
import { snapToNearestRoad } from "@/lib/googleRoads";
import { PointsOfInterest, RM_TAGS } from "@/redux/apis/clientPoi/types";

/** `Terminal 3` → `3`. The designator, for building the query and the label. */
const terminalToken = (label: string) => label.replace(/^Terminal\s+/i, "");

/**
 * The flight's arrival terminal as a Google-resolved POI — the fallback for an operator whose provider
 * POIs carry no row for it (`findProviderTerminalPoi` first, always).
 *
 * Reads `window.google.maps.places` rather than taking the services as arguments, exactly as
 * `LocationPanel`'s geocode does, so nothing here loads Google: the caller holds the single
 * `useLoadScript` the Map atom and `useAddressSearch` share, and only calls in once it reports loaded.
 * That also makes this a plain async function a test can drive against a stubbed namespace.
 *
 * The resulting POI is relabelled rather than left as Google returned it. `googlePlaceToPoi` yields an
 * empty `rm_label` and no tags (a Google prediction has an address, not a name), but the sheet reads the
 * terminal and the IATA back OUT of the label — `extractTerminal` / `extractAirportIata` drive the
 * left-panel terminal row, the leg picker on multi-leg flights, and the idempotence check that stops this
 * swap running twice. An unlabelled airport POI would break all three.
 */
export const findGoogleTerminalPoi = async (
  arrival: FlightArrivalTerminal,
  /** The autocomplete's country restriction (`defaultCountrySelector`), as the address search applies it. */
  country?: string | null
): Promise<PointsOfInterest | null> => {
  const label = flightTerminalLabel(arrival.terminal);
  const places = window.google?.maps?.places;
  if (!label || !arrival.iata || !arrival.name || !places) return null;

  const token = terminalToken(label);
  // Wrapped whole rather than per call: the two `new places.*Service()` constructors run inside promise
  // executors, so a Places namespace that is present but broken would reject out of the caller's effect
  // as an unhandled rejection. A failed lookup is a MISS — the pickup simply stays where the client put
  // it — which is the same graceful degradation `snapToNearestRoad` gives the rest of the address flow.
  try {
    const predictions = await new Promise<google.maps.places.AutocompletePrediction[]>((resolve) => {
      new places.AutocompleteService().getPlacePredictions(
        {
          input: `${arrival.name} Terminal ${token}`,
          ...(country ? { componentRestrictions: { country } } : {}),
        },
        (results) => resolve(results ?? [])
      );
    });

    // Google happily answers "Sydney Airport Terminal 3" with the airport itself, which would swap the
    // pickup to a POI carrying no terminal at all — and then re-run for ever, since the pickup would still
    // not match the flight. Only a prediction that names THIS terminal is the terminal.
    const match = predictions.find((prediction) => terminalFromText(prediction.description) === label);
    if (!match) return null;

    const details = await new Promise<google.maps.places.PlaceResult | null>((resolve) => {
      new places.PlacesService(document.createElement("div")).getDetails(
        { placeId: match.place_id, fields: ["geometry.location", "address_components"] },
        (result) => resolve(result)
      );
    });

    const location = details?.geometry?.location;
    if (!location) return null;
    // Snap to the nearest road before committing it, as every other picked address is (mobile parity).
    const snapped = await snapToNearestRoad({ lat: location.lat(), lng: location.lng() });
    const poi = googlePlaceToPoi(
      { description: match.description, place_id: match.place_id, types: match.types },
      snapped,
      parseGoogleAddressComponents(details?.address_components ?? [])
    );
    return {
      ...poi,
      rm_label: `${arrival.name} Terminal T${token} (${arrival.iata})`,
      rm_tags: [RM_TAGS.Airport, RM_TAGS.FixedPin],
    };
  } catch {
    return null;
  }
};
